import * as React from "react";

import { cn } from "@/lib/utils";
import { Input } from "./input";
import { Label } from "./label";

export interface FormFieldProps
  extends Omit<React.ComponentProps<"input">, "onChange"> {
  id: string;
  label: React.ReactNode;
  error?: string | null;
  hint?: React.ReactNode;
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  inputClassName?: string;
  fieldClassName?: string;
  rightSlot?: React.ReactNode;
}

export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      id,
      label,
      error,
      hint,
      onChange,
      inputClassName,
      fieldClassName,
      rightSlot,
      ...inputProps
    },
    ref,
  ) => {
    const hasError = Boolean(error);
    const describedBy = hasError ? `${id}-error` : hint ? `${id}-hint` : undefined;

    return (
      <div className={cn("space-y-2", fieldClassName)}>
        <Label htmlFor={id}>{label}</Label>
        <div className={cn(rightSlot ? "relative" : undefined)}>
          <Input
            ref={ref}
            id={id}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            onChange={
              onChange ? (event) => onChange(event.target.value, event) : undefined
            }
            className={cn(
              "bg-background border-border",
              hasError && "border-destructive focus-visible:ring-destructive",
              rightSlot && "pr-10",
              inputClassName,
            )}
            {...inputProps}
          />
          {rightSlot && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-1">
              {rightSlot}
            </div>
          )}
        </div>
        {hasError ? (
          <p id={`${id}-error`} className="text-xs text-destructive">
            {error}
          </p>
        ) : hint ? (
          <p id={`${id}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
FormField.displayName = "FormField";
